/** @jsx jsx */
import type { ReactNode } from 'react';
import { jsx, css } from '@compiled/react';
import { xcss, Grid } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { HorizontalNavSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import {
	Tokens,
	NAV4_HORIZONTAL_NAV_HEIGHT,
} from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';

type Props = {
	children: ReactNode;
};
const NavWrapper = ({ children }: Props) => {
	const [isFullscreen] = useIsFullscreen();
	const { isSoftwareProject } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareProject: false };

	return (
		<UFOSegment name="jira-horizontal-nav">
			<JSErrorBoundary
				id="HorizontalNavErrorBoundary"
				packageName="jiraHorizontalNav"
				fallback={HorizontalNavSkeleton}
			>
				<header
					id="ak-project-view-navigation"
					css={[!isSoftwareProject && headerStyles]}
					data-testid="horizontal-nav.ui.content.horizontal-nav"
					{...(__SERVER__
						? { 'data-ssr-placeholder': 'project-view-navigation' }
						: { 'data-ssr-placeholder-replace': 'project-view-navigation' })}
				>
					<Grid
						gap="space.050"
						alignItems="end"
						xcss={[
							contentStyles,
							!isFullscreen && minHeightStyle,
							!isSoftwareProject && relativePositionStyle,
						]}
					>
						{children}
					</Grid>
				</header>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

export default NavWrapper;

const minHeightStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	minHeight: `${NAV4_HORIZONTAL_NAV_HEIGHT}px`,
});

const contentStyles = xcss({
	paddingTop: 'space.150',
});

// Delete headerStyles when business projects are migrated to the new theming
const headerStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: Tokens.ELEVATION_SURFACE_CONTRAST,
	boxSizing: 'border-box',
	position: 'relative',
	'&::before': {
		content: ' ',
		position: 'absolute',
		height: '100%',
		width: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backdropFilter: Tokens.BACKDROP_FILTER,
		top: 0,
		left: 0,
	},
});

// Delete relativePositionStyle when business projects are migrated to the new theming
const relativePositionStyle = xcss({
	position: 'relative',
});
